import { uuidv4Regex } from "common/consts";
import { useInfoDisplayMessageWebSocket } from "common/hooks/useInfoDisplaySocket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { MessageAndPlates } from "views/infoDisplay/MessageAndPlates";
import {
  DisplayPlate,
  PlateEntranceExitPayload,
  SettingsPayload,
} from "views/infoDisplay/types";
import { InfoDisplayMessageType } from "../../../../backend/src/integration/infoDisplay/types";

const directions = ["in", "out", "any"] as const;

type ConfigDirection = (typeof directions)[number];

const getFocusPlate = (plates: DisplayPlate[]) => {
  const now = Date.now();
  const futurePlates = plates.filter(
    ({ focusEnd }) => focusEnd.getTime() > now
  );
  const closest =
    futurePlates.length > 0
      ? futurePlates.reduce((prev, curr) => {
          return prev.focusEnd.getTime() < curr.focusEnd.getTime()
            ? prev
            : curr;
        })
      : undefined;
  return closest;
};

const useFocusPlate = (plates: DisplayPlate[]) => {
  const [focusPlate, setFocusPlate] = useState<DisplayPlate | undefined>(
    getFocusPlate(plates)
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const closest = getFocusPlate(plates);
    setFocusPlate(closest);

    if (closest) {
      const diff = Math.max(0, closest.focusEnd.getTime() - Date.now());
      timeoutRef.current = setTimeout(() => {
        setFocusPlate(getFocusPlate(plates));
      }, diff);
    }

    return () => clearTimeout(timeoutRef.current);
  }, [plates, focusPlate]);

  return focusPlate;
};

const useTimedPlates = (maxShowTimeMs: number) => {
  const [plates, setPlates] = useState<DisplayPlate[]>([]);

  const startTimer = useCallback(
    (plate: DisplayPlate) => {
      setTimeout(() => {
        setPlates((prev) => {
          if (prev.includes(plate)) return prev.filter((p) => p !== plate);
          return prev;
        });
      }, maxShowTimeMs);
    },
    [maxShowTimeMs]
  );

  const addPlate = useCallback(
    (newPlate: DisplayPlate) => {
      setPlates((prev) => {
        const updated = [];
        let append = true;
        prev.forEach((plate) => {
          if (plate.payload.vehicleId !== newPlate.payload.vehicleId) {
            updated.push(plate);
          } else if (plate.payload.plateText === newPlate.payload.plateText) {
            append = false;
            updated.push(plate);
          }
        });
        if (append) {
          updated.push(newPlate);
          startTimer(newPlate);
        }
        return updated;
      });
    },
    [startTimer]
  );

  return { plates, addPlate };
};

export const InfoDisplayEntranceExitView = ({
  settings,
}: {
  settings: SettingsPayload;
}) => {
  const { plates, addPlate } = useTimedPlates(settings.maxShowTimeMs);
  const plateFocus = useFocusPlate(plates);
  const { plates: overparkingPlates, addPlate: addOverparkingPlate } =
    useTimedPlates(settings.maxShowTimeMs);
  const overparkingFocus = useFocusPlate(overparkingPlates);
  const { search } = useLocation();

  const connectedLanesDirections = useMemo(() => {
    const iterator = search.matchAll(/(\?|&)lanesDirections=(.*?)(&|$)/g);
    const [, , matches = ""] = Array.from(iterator)[0] || [];
    const entries = matches.split(",").reduce((acc, v, idx, arr) => {
      const prev = arr[idx - 1];

      if (!(idx % 2)) return acc;

      const laneIdValid = prev === "any" || (prev && prev.match(uuidv4Regex));
      const directionValid = directions.includes(v as ConfigDirection);

      if (laneIdValid && directionValid) {
        acc.push({
          laneId: prev.toLowerCase(),
          direction: v.toLowerCase() as ConfigDirection,
        });
      } else {
        console.error(
          "Connected lanes and directions configuration value is not valid:",
          `${prev},${v}`
        );
      }

      return acc;
    }, [] as { laneId: string; direction: ConfigDirection }[]);

    return entries || [];
  }, [search]);

  const shouldNotify = useCallback(
    (payload: PlateEntranceExitPayload) => {
      if (!connectedLanesDirections.length) return true;

      const { laneId, direction } = payload;
      const matched = connectedLanesDirections
        .filter((ld) => ld.laneId === "any" || ld.laneId === laneId)
        .filter((ld) => ld.direction === "any" || ld.direction === direction);

      return !!matched.length;
    },
    [connectedLanesDirections]
  );

  const handlePlateEntranceExitMessage = useCallback(
    (payload: PlateEntranceExitPayload) => {
      if (!shouldNotify(payload)) return;

      const newPlate = {
        payload,
        focusEnd: new Date(Date.now() + settings.waitTimeMs),
      };

      if (payload.isOverparking) {
        addOverparkingPlate(newPlate);
      } else {
        addPlate(newPlate);
      }
    },
    [shouldNotify, settings.waitTimeMs, addOverparkingPlate, addPlate]
  );

  // uncomment for test messages
  //useTestMessages(handlePlateEntranceExitMessage);

  useInfoDisplayMessageWebSocket(
    InfoDisplayMessageType.PlateEntranceOrExit,
    handlePlateEntranceExitMessage
  );

  return (
    <View>
      {overparkingPlates.length > 0 && (
        <MessageAndPlates
          plates={overparkingPlates}
          focusPlate={overparkingFocus}
          maxShowTimeMs={settings.maxShowTimeMs}
          simultaneousPlates={settings.simultaneousPlates}
        />
      )}
      {!overparkingPlates.length &&
        (plates.length > 0 || settings.staticEntranceMessage) && (
          <MessageAndPlates
            plates={plates}
            focusPlate={plateFocus}
            maxShowTimeMs={settings.maxShowTimeMs}
            simultaneousPlates={settings.simultaneousPlates}
            staticMessage={settings.staticEntranceMessage}
          />
        )}
    </View>
  );
};

const View = styled.div`
  flex: 1 0 0%;
`;
